import tokenManager from '../utils/tokenManager';
import { getAnonymousId, getSessionId } from '../utils/productAnalytics';
import { logger } from '../utils/logger';

/**
 * 行動分析 SDK のコア。
 *
 * イベントをキューに貯め、5 秒ごと / 20 件で POST /api/analytics/events へ送る。
 * ページ離脱時は navigator.sendBeacon（text/plain・認証なし）で送り、サーバー側が
 * 同一 session_id で user を後追い紐付けする。識別子（anonymous_id / session_id）は
 * 既存の productAnalytics と共用する。
 *
 * 計測の失敗は UX に影響させない（fetch 失敗は握り潰して再送、それ以外は logger のみ）。
 * user_id / organization_id は送らない（サーバーがトークンから確定する）。
 *
 * 生成の完了/失敗（generation_completed / generation_failed）はここからは送らない。
 * サーバー側の GenerationOutcomeRecorder が記録する。
 */

export type BehaviorEventType =
  | 'page_view'
  | 'page_leave'
  | 'click'
  | 'step'
  | 'action'
  | 'api_error'
  | 'api_timing'
  | 'js_error';

export interface BehaviorEventInput {
  event_type: BehaviorEventType;
  event_name: string;
  page_path?: string;
  page_title?: string;
  referrer_path?: string;
  duration_ms?: number;
  props?: Record<string, unknown>;
  occurred_at?: string;
}

export interface BehaviorEvent extends BehaviorEventInput {
  event_id: string;
  occurred_at: string;
  anonymous_id: string;
  session_id: string;
  page_path: string;
}

export const FLUSH_INTERVAL_MS = 5000;
export const FLUSH_THRESHOLD = 20;
export const MAX_BATCH = 50;
export const MAX_QUEUE = 500;
export const EVENT_NAME_MAX = 150;
export const EXCLUDED_PATH_PREFIXES = ['/admin', '/design-system', '/test'];
export const MIRRORED_TYPES: BehaviorEventType[] = ['page_view', 'step', 'action'];

let queue: BehaviorEvent[] = [];
let flushing = false;

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

export const isTrackingEnabled = (): boolean =>
  isBrowser() && process.env.REACT_APP_BEHAVIOR_TRACKING !== 'off';

export const isExcludedPath = (path: string): boolean =>
  EXCLUDED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

const createUuid = (): string => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const endpoint = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
  const baseURL = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
  return `${baseURL}/analytics/events`;
};

const requeue = (batch: BehaviorEvent[]): void => {
  queue = [...batch, ...queue].slice(-MAX_QUEUE);
};

/**
 * GTM / GA4 用に dataLayer へも流す（page_view / step / action のみ。クリックは流さない）。
 */
export const mirrorToDataLayer = (event: BehaviorEvent): void => {
  if (!MIRRORED_TYPES.includes(event.event_type)) return;
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: `tk_${event.event_type}`,
      tk_name: event.event_name,
      tk_props: event.props ?? {},
    });
  } catch {
    // dataLayer が使えない環境では無視する。
  }
};

export const enqueue = (input: BehaviorEventInput): void => {
  if (!isTrackingEnabled()) return;
  try {
    const event: BehaviorEvent = {
      event_type: input.event_type,
      event_name: input.event_name.slice(0, EVENT_NAME_MAX),
      event_id: createUuid(),
      occurred_at: input.occurred_at ?? new Date().toISOString(),
      anonymous_id: getAnonymousId(),
      session_id: getSessionId(),
      page_path: input.page_path ?? window.location.pathname,
      page_title: input.page_title,
      referrer_path: input.referrer_path,
      duration_ms: input.duration_ms,
      props: input.props && Object.keys(input.props).length > 0 ? input.props : undefined,
    };

    queue.push(event);
    if (queue.length > MAX_QUEUE) {
      queue = queue.slice(-MAX_QUEUE);
    }
    mirrorToDataLayer(event);

    if (queue.length >= FLUSH_THRESHOLD) {
      void flush();
    }
  } catch (error) {
    logger.error('behaviorTracker enqueue failed', error);
  }
};

/**
 * キューの先頭 MAX_BATCH 件を送る。ネットワーク失敗と 5xx / 429 は再送（先頭に戻す）、
 * 4xx は捨てる（送り直しても通らない）。
 */
export const flush = async (): Promise<void> => {
  if (!isBrowser() || queue.length === 0 || flushing) return;

  flushing = true;
  const batch = queue.splice(0, MAX_BATCH);

  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = tokenManager.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(endpoint(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });

    if (response.status >= 500 || response.status === 429) {
      requeue(batch);
    }
  } catch (error) {
    requeue(batch);
    logger.error('behaviorTracker flush failed', error);
  } finally {
    flushing = false;
  }
};

/**
 * ページ離脱時の送信。sendBeacon は Content-Type を自由にできないので text/plain で送り、
 * サーバー側で生ボディを JSON として読む。使えない環境では通常の flush に落とす。
 */
export const flushWithBeacon = (): void => {
  if (!isBrowser() || queue.length === 0) return;

  const batch = queue.splice(0, MAX_BATCH);
  let sent = false;

  try {
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify({ events: batch })], { type: 'text/plain' });
      sent = navigator.sendBeacon(endpoint(), blob);
    }
  } catch {
    sent = false;
  }

  if (!sent) {
    requeue(batch);
    void flush();
  }
};

/**
 * 定期送信と離脱時送信を開始する。戻り値で解除。
 */
export const start = (): (() => void) => {
  if (!isTrackingEnabled()) return () => {};

  const timer = window.setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      flushWithBeacon();
    }
  };
  const onPageHide = (): void => {
    flushWithBeacon();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    void flush();
  };
};

export const pendingCount = (): number => queue.length;

export const _resetForTests = (): void => {
  queue = [];
  flushing = false;
};
